import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Download, MoreVertical, Music, Trash2, Loader2, Clock, Plus, Upload, Sparkles, Mic2, Wand2, BarChart3, Scissors, FileMusic, FileText, History as HistoryIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import SessionDetailsModal from '../components/SessionDetailsModal';

const PROCESSING_STATUSES = ['uploading', 'processing', 'separating', 'transcribing', 'detecting', 'finalizing'];

export default function History() {
  const [sessions, setProjects] = useState([]);
  const [currentSessions, setCurrentSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalSessions, setTotalSessions] = useState(0);
  const SESSIONS_PER_PAGE = 10;
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProjects();
    const interval = setInterval(fetchProjects, 5000);
    return () => clearInterval(interval);
  }, [currentPage]);

  const fetchProjects = async () => {
    try {
      const response = await api.get(`/sessions?page=${currentPage}&limit=${SESSIONS_PER_PAGE}`);
      const { sessions: allSessions, total, total_pages } = response.data;
      const processing = allSessions.filter(s => PROCESSING_STATUSES.includes(s.status) || (s.status === 'complete' && !s.files?.clean_url));
      const completed = allSessions.filter(s => s.files?.clean_url);
      setCurrentSessions(processing);
      setProjects(completed);
      setTotalPages(total_pages);
      setTotalSessions(total);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      setSelectedSessions([]);
    }
  };

  const handleEditSession = (session) => {
    navigate(`/editor/${session.id}`);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const handleSessionClick = (session, event) => {
    // Don't open modal if clicking checkbox or dropdown
    if (event.target.closest('button') || event.target.closest('[role="checkbox"]')) {
      return;
    }
    setSelectedSession(session);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSession(null);
  };

  const handleSelectSession = (sessionId) => {
    setSelectedSessions(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      } else {
        return [...prev, sessionId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSessions.length === sessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(sessions.map(s => s.id));
    }
  };

  const handleDeleteClick = () => {
    if (selectedSessions.length === 0) return;
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      // Delete each selected session
      await Promise.all(
        selectedSessions.map(sessionId => 
          api.delete(`/sessions/${sessionId}`)
        )
      );

      toast.success(`${selectedSessions.length} session${selectedSessions.length > 1 ? 's' : ''} deleted successfully`);
      setSelectedSessions([]);
      setShowDeleteDialog(false);
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete sessions');
      console.error('Delete error:', error);
    }
  };

  return (
    <div className="flex-1">
      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              data-testid="back-button"
              className="h-8 px-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Sessions</h1>
              {selectedSessions.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {selectedSessions.length} selected
                </p>
              )}
            </div>
          </div>
          {selectedSessions.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteClick}
              data-testid="delete-selected-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Selected
            </Button>
          )}
        </div>
        {/* Current Sessions - Processing or Ready to Edit */}
        {currentSessions.length > 0 && (
          <Card data-testid="current-sessions">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Current Sessions
              </CardTitle>
              <CardDescription>Sessions currently processing or ready to edit</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {currentSessions.map((session) => (
                  <div
                    key={session.id}
                    onClick={() => session.status === 'complete' ? navigate(`/editor/${session.id}`) : null}
                    className={`flex items-center justify-between p-4 rounded-lg border bg-card ${
                      session.status === 'complete' ? 'cursor-pointer hover:border-primary/50 hover:bg-secondary/30' : ''
                    } transition-colors`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Music className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{session.title || session.filename}</p>
                        <p className="text-sm text-muted-foreground">{session.artist || 'Unknown Artist'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {PROCESSING_STATUSES.includes(session.status) ? (
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          {session.status.charAt(0).toUpperCase() + session.status.slice(1)}...
                        </Badge>
                      ) : (
                        <Badge variant="default" className="bg-green-500/10 text-green-500 hover:bg-green-500/20">
                          Ready to Edit
                        </Badge>
                      )}
                      {session.duration && (
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTime(session.duration)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Completed Sessions */}
        <Card data-testid="history-table">
          <CardHeader>
            <CardTitle>All Sessions</CardTitle>
            <CardDescription>View and download all your processed audio files</CardDescription>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Music className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No sessions yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedSessions.length === sessions.length && sessions.length > 0}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Artist</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Curses Detected</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow 
                      key={session.id} 
                      onClick={(e) => handleSessionClick(session, e)}
                      className="cursor-pointer hover:bg-secondary/50 transition-colors"
                      data-testid={`history-row-${session.id}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedSessions.includes(session.id)}
                          onCheckedChange={() => handleSelectSession(session.id)}
                          data-testid={`select-session-${session.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{session.title || session.filename}</TableCell>
                      <TableCell className="text-muted-foreground">{session.artist || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{formatTime(session.duration)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {session.regions?.length || 0}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(session.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`history-actions-${session.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {session.files?.original_url && (
                              <DropdownMenuItem
                                onClick={() => window.open(session.files.original_url, '_blank')}
                                data-testid="history-download-original-button"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download Original
                              </DropdownMenuItem>
                            )}
                            {session.files?.vocal_url && (
                              <DropdownMenuItem
                                onClick={() => window.open(session.files.vocal_url, '_blank')}
                                data-testid="history-download-vocals-button"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download Vocals
                              </DropdownMenuItem>
                            )}
                            {session.files?.instrumental_url && (
                              <DropdownMenuItem
                                onClick={() => window.open(session.files.instrumental_url, '_blank')}
                                data-testid="history-download-instrumental-button"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download Instrumental
                              </DropdownMenuItem>
                            )}
                            {session.files?.clean_url && (
                              <DropdownMenuItem
                                onClick={() => window.open(session.files.clean_url, '_blank')}
                                data-testid="history-download-clean-button"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download Clean
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * SESSIONS_PER_PAGE) + 1} - {Math.min(currentPage * SESSIONS_PER_PAGE, totalSessions)} of {totalSessions} sessions
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        if (totalPages <= 5) return true;
                        if (page === 1 || page === totalPages) return true;
                        if (Math.abs(page - currentPage) <= 1) return true;
                        return false;
                      })
                      .map((page, index, arr) => (
                        <React.Fragment key={page}>
                          {index > 0 && arr[index - 1] !== page - 1 && (
                            <span className="px-1 text-muted-foreground">...</span>
                          )}
                          <Button
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            className="w-8 h-8 p-0"
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Button>
                        </React.Fragment>
                      ))
                    }
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Start Here Section */}
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Start Here
            </CardTitle>
            <CardDescription className="text-base">
              Upload any song to unlock AI-powered audio tools
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Upload CTA */}
            <div 
              onClick={() => navigate('/dashboard')}
              className="group relative flex h-40 items-center justify-center rounded-lg border-2 border-dashed border-primary/30 hover:border-primary/60 bg-background/50 hover:bg-background/80 transition-all cursor-pointer"
            >
              <div className="text-center space-y-3">
                <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Upload className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-primary">Click to upload a song</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    MP3, WAV, FLAC, AAC, OGG, M4A, AIFF supported
                  </p>
                </div>
              </div>
            </div>

            {/* Start Here */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Start Here - Upload Any Song To:
              </h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-8 w-8 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <Mic2 className="h-4 w-4 text-purple-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Separate Vocals</p>
                    <p className="text-xs text-muted-foreground">
                      Isolate vocals from instrumentals
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-8 w-8 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <Wand2 className="h-4 w-4 text-red-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Clean Explicit Content</p>
                    <p className="text-xs text-muted-foreground">
                      Auto-detect and censor profanity
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-8 w-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-4 w-4 text-green-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Analyze Your Track</p>
                    <p className="text-xs text-muted-foreground">
                      Get BPM, key, genre, mood & structure
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-8 w-8 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                    <Scissors className="h-4 w-4 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Extract Stems</p>
                    <p className="text-xs text-muted-foreground">
                      Separate drums, bass, guitar, piano & more
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-8 w-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-4 w-4 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Transcribe Lyrics</p>
                    <p className="text-xs text-muted-foreground">
                      AI-powered accurate transcription
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-8 w-8 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                    <FileMusic className="h-4 w-4 text-cyan-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Convert to MIDI</p>
                    <p className="text-xs text-muted-foreground">
                      Turn any audio into MIDI notes
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Button 
              onClick={() => navigate('/dashboard')} 
              className="w-full"
              size="lg"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload a Song
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Session Details Modal */}
      <SessionDetailsModal 
        session={selectedSession}
        open={showModal}
        onClose={handleCloseModal}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sessions</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedSessions.length} session{selectedSessions.length > 1 ? 's' : ''}? 
              This action cannot be undone. All associated files and data will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
